import React from 'react';
import VideoPlayer from '../VideoPlayer.js';
import VideoGrid from '../VideoGrid.js';
import Sidebar from '../Sidebar.js';
import { fetchCategorizedVideos } from '../../utils/videoUtils.js';
import {
  CATEGORY_TITLES,
  INITIAL_VISIBLE_COUNT,
  LOAD_MORE_COUNT,
  ROTATION_INTERVAL_MS,
} from '../../constants.js';

const itemTagsCache = new WeakMap();
const videoTextCache = new WeakMap();

const getCategoryVideos = (categoryData) => {
  if (!categoryData) {
    return [];
  }
  if (Array.isArray(categoryData)) {
    return categoryData;
  }
  if (Array.isArray(categoryData.videos)) {
    return categoryData.videos;
  }
  return [];
};

const findFirstAvailableVideo = (categories) => {
  if (!categories) {
    return null;
  }
  for (const category of Object.keys(categories)) {
    const videos = getCategoryVideos(categories[category]);
    if (videos.length > 0) {
      return videos[0];
    }
  }
  return null;
};

const buildWindowedVideos = (videos, startIndex, windowSize) => {
  if (!Array.isArray(videos) || videos.length === 0) {
    return [];
  }
  const safeWindow = Math.min(windowSize, videos.length);
  const items = [];
  for (let i = 0; i < safeWindow; i += 1) {
    const index = (startIndex + i) % videos.length;
    items.push(videos[index]);
  }
  return items;
};

const normalizeHashtag = (tag) => {
  if (!tag) {
    return '';
  }
  const trimmed = tag.trim().toLowerCase();
  return trimmed.startsWith('#') ? trimmed : `#${trimmed}`;
};

const getVideoId = (video) => {
  if (!video) {
    return '';
  }
  if (typeof video.id === 'string') {
    return video.id;
  }
  if (video.id && typeof video.id === 'object') {
    return video.id.videoId || video.id.id || '';
  }
  return '';
};

const getVideoText = (video) => {
  return [
    video?.snippet?.title,
    video?.snippet?.description,
    video?.title,
    video?.description,
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
};

const getVideoTags = (video) => {
  if (Array.isArray(video?.snippet?.tags)) {
    return video.snippet.tags;
  }
  if (Array.isArray(video?.tags)) {
    return video.tags;
  }
  if (typeof video?.tags === 'string') {
    return video.tags.split(',').map((tag) => tag.trim()).filter(Boolean);
  }
  return [];
};

const videoHasHashtag = (searchCriteria, itemTags, getLazyText) => {
  if (!searchCriteria) {
    return false;
  }
  const { normalized, withHash } = searchCriteria;
  if (itemTags.includes(normalized)) {
    return true;
  }
  if (itemTags.includes(withHash)) {
    return true;
  }
  const text = getLazyText();
  return text.includes(normalized) || text.includes(withHash);
};

const flattenVideos = (categories) => {
  const flattened = [];
  const seen = new Set();
  Object.keys(categories || {}).forEach((category) => {
    const videos = getCategoryVideos(categories[category]);
    videos.forEach((video) => {
      const id = getVideoId(video);
      if (id && seen.has(id)) {
        return;
      }
      if (id) {
        seen.add(id);
      }
      flattened.push(video);
    });
  });
  return flattened;
};

/**
 * Home page component with video gallery
 */
const Home = () => {
  const [categorizedVideos, setCategorizedVideos] = React.useState({});
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);
  const [currentVideo, setCurrentVideo] = React.useState(null);
  const [visibleOffsets, setVisibleOffsets] = React.useState({});
  const [isUserOverride, setIsUserOverride] = React.useState(false);
  const [hashtagFilters, setHashtagFilters] = React.useState({});
  const rotationTimerRef = React.useRef(null);
  const rotationIndexRef = React.useRef(0);

  const loadVideos = React.useCallback(async () => {
    try {
      setLoading(true);
      const data = await fetchCategorizedVideos();
      setCategorizedVideos(data || {});
      setError(null);
    } catch (err) {
      setError(err.message);
      console.error('Error fetching categorized videos:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => {
    loadVideos();
    return () => {
      if (rotationTimerRef.current) {
        clearInterval(rotationTimerRef.current);
      }
    };
  }, [loadVideos]);

  const firstAsmrVideos = React.useMemo(() => {
    return getCategoryVideos(categorizedVideos.firstasmr);
  }, [categorizedVideos]);

  React.useEffect(() => {
    if (loading) {
      return;
    }
    if (currentVideo) {
      return;
    }
    if (firstAsmrVideos.length > 0) {
      setCurrentVideo(firstAsmrVideos[0]);
      return;
    }
    const fallback = findFirstAvailableVideo(categorizedVideos);
    if (fallback) {
      setCurrentVideo(fallback);
    }
  }, [loading, currentVideo, firstAsmrVideos, categorizedVideos]);

  React.useEffect(() => {
    return () => {
      if (rotationTimerRef.current) {
        clearInterval(rotationTimerRef.current);
        rotationTimerRef.current = null;
      }
    };
  }, []);

  React.useEffect(() => {
    setVisibleOffsets((previous) => {
      const next = {};
      let changed = false;
      Object.keys(categorizedVideos || {}).forEach((category) => {
        const videos = getCategoryVideos(categorizedVideos[category]);
        if (videos.length === 0) {
          return;
        }
        const previousStart = previous[category] || 0;
        const normalizedStart = previousStart % videos.length;
        next[category] = normalizedStart;
        if (normalizedStart !== previousStart) {
          changed = true;
        }
      });
      if (!changed) {
        const previousKeys = Object.keys(previous);
        const nextKeys = Object.keys(next);
        if (previousKeys.length !== nextKeys.length) {
          changed = true;
        } else {
          for (const key of nextKeys) {
            if (previous[key] !== next[key]) {
              changed = true;
              break;
            }
          }
        }
      }
      return changed ? next : previous;
    });
  }, [categorizedVideos]);

  React.useEffect(() => {
    if (rotationTimerRef.current) {
      clearInterval(rotationTimerRef.current);
      rotationTimerRef.current = null;
    }

    if (isUserOverride || firstAsmrVideos.length === 0) {
      return;
    }

    rotationIndexRef.current = 0;
    setCurrentVideo(firstAsmrVideos[0]);

    rotationTimerRef.current = setInterval(() => {
      rotationIndexRef.current = (rotationIndexRef.current + 1) % firstAsmrVideos.length;
      setCurrentVideo(firstAsmrVideos[rotationIndexRef.current]);
    }, ROTATION_INTERVAL_MS);

    return () => {
      if (rotationTimerRef.current) {
        clearInterval(rotationTimerRef.current);
        rotationTimerRef.current = null;
      }
    };
  }, [firstAsmrVideos, isUserOverride]);

  // ⚡ Bolt: Memoize handlers passed to React.memo child components
  // so they maintain stable references across re-renders
  const handleVideoClick = React.useCallback((video) => {
    setIsUserOverride(true);
    setCurrentVideo(video);
  }, []);

  const handleFilterChange = React.useCallback((tag, value) => {
    setHashtagFilters((previous) => ({ ...previous, [tag]: value }));
  }, []);

  const handleResetFilters = React.useCallback(() => {
    setHashtagFilters({});
  }, []);

  const { includeTags, excludeTags, hasActiveFilters } = React.useMemo(() => {
    const include = [];
    const exclude = [];
    Object.entries(hashtagFilters).forEach(([tag, state]) => {
      if (state === 'yes') {
        include.push(tag);
      } else if (state === 'no') {
        exclude.push(tag);
      }
    });
    return {
      includeTags: include,
      excludeTags: exclude,
      hasActiveFilters: include.length > 0 || exclude.length > 0,
    };
  }, [hashtagFilters]);

  const allVideos = React.useMemo(() => {
    if (!hasActiveFilters) {
      return [];
    }
    return flattenVideos(categorizedVideos);
  }, [categorizedVideos, hasActiveFilters]);

  const filteredVideos = React.useMemo(() => {
    if (!hasActiveFilters) {
      return [];
    }
    // ⚡ Bolt: Lifted .replace() out of the filter loop
    const prepareSearchCriteria = (tag) => {
      const normalized = normalizeHashtag(tag);
      if (!normalized) return null;
      return {
        normalized,
        withHash: `#${normalized.replace('#', '')}`,
      };
    };

    const normalizedExclude = excludeTags.map(prepareSearchCriteria).filter(Boolean);
    const normalizedInclude = includeTags.map(prepareSearchCriteria).filter(Boolean);

    return allVideos.filter((video) => {
      let itemTags = itemTagsCache.get(video);
      if (itemTags === undefined) {
        itemTags = getVideoTags(video).map((entry) => normalizeHashtag(entry));
        itemTagsCache.set(video, itemTags);
      }
      let cachedText = null;
      const getLazyText = () => {
        if (cachedText === null) {
          cachedText = videoTextCache.get(video);
          if (cachedText === undefined) {
            cachedText = getVideoText(video);
            videoTextCache.set(video, cachedText);
          }
        }
        return cachedText;
      };

      // ⚡ Bolt: Replaced .some() and .every() with native for loops to avoid
      // closure instantiation and intermediate array allocation overhead in O(N) paths.
      if (normalizedExclude.length > 0) {
        for (let i = 0; i < normalizedExclude.length; i++) {
          if (videoHasHashtag(normalizedExclude[i], itemTags, getLazyText)) {
            return false;
          }
        }
      }

      if (normalizedInclude.length === 0) {
        return true;
      }

      for (let i = 0; i < normalizedInclude.length; i++) {
        if (!videoHasHashtag(normalizedInclude[i], itemTags, getLazyText)) {
          return false;
        }
      }

      return true;
    });
  }, [allVideos, excludeTags, includeTags, hasActiveFilters]);

  React.useEffect(() => {
    if (!hasActiveFilters) {
      return;
    }
    setIsUserOverride(true);
  }, [hasActiveFilters]);

  React.useEffect(() => {
    if (!hasActiveFilters) {
      return;
    }
    if (!filteredVideos.length) {
      return;
    }
    const currentId = getVideoId(currentVideo);
    const isCurrentVideoInFiltered = filteredVideos.some(
      (video) => getVideoId(video) === currentId
    );
    if (!currentId || !isCurrentVideoInFiltered) {
      setCurrentVideo(filteredVideos[0]);
    }
  }, [filteredVideos, currentVideo, hasActiveFilters]);

  const handleLoadMore = React.useCallback((category) => {
    setVisibleOffsets((previous) => {
      const videos = getCategoryVideos(categorizedVideos[category]);
      if (videos.length === 0) {
        return previous;
      }
      const windowSize = Math.min(INITIAL_VISIBLE_COUNT, videos.length);
      if (videos.length <= windowSize) {
        return previous;
      }
      const increment = Math.min(LOAD_MORE_COUNT, videos.length);
      const currentStart = previous[category] || 0;
      const nextStart = (currentStart + increment) % videos.length;
      if (nextStart === currentStart) {
        return previous;
      }
      return { ...previous, [category]: nextStart };
    });
  }, [categorizedVideos]);

  const handleLoadPrevious = React.useCallback((category) => {
    setVisibleOffsets((previous) => {
      const videos = getCategoryVideos(categorizedVideos[category]);
      if (videos.length === 0) {
        return previous;
      }
      const windowSize = Math.min(INITIAL_VISIBLE_COUNT, videos.length);
      if (videos.length <= windowSize) {
        return previous;
      }
      const decrement = Math.min(LOAD_MORE_COUNT, videos.length);
      const currentStart = previous[category] || 0;
      const nextStart = (currentStart - decrement + videos.length) % videos.length;
      if (nextStart === currentStart) {
        return previous;
      }
      return { ...previous, [category]: nextStart };
    });
  }, [categorizedVideos]);

  // ⚡ Bolt: Memoize the video sections to prevent recreating large DOM structures
  // and maintain stable props for React.memo children when currentVideo updates
  const videoSections = React.useMemo(() => {
    const sections = [];
    if (!loading && categorizedVideos) {
      const renderedTitles = new Set();
      Object.keys(categorizedVideos).forEach((category) => {
        const categoryData = categorizedVideos[category];
        const videos = getCategoryVideos(categoryData);
        const title = (categoryData && categoryData.title) || CATEGORY_TITLES[category] || category;
        if (videos.length === 0 || renderedTitles.has(title)) {
          return;
        }
        renderedTitles.add(title);
        const startIndex = visibleOffsets[category] || 0;
        const visibleVideos = buildWindowedVideos(videos, startIndex, INITIAL_VISIBLE_COUNT);
        const windowSize = Math.min(INITIAL_VISIBLE_COUNT, videos.length);
        const hasMore = videos.length > windowSize;

        sections.push(
          <section key={category} className="video-section mb-5">
            <div className="video-section__header">
              <h3>{title}</h3>
            </div>
            <div className="video-section__content">
              {hasMore && (
                <button
                  type="button"
                  className="prev-button"
                  onClick={() => handleLoadPrevious(category)}
                  aria-label={`Load previous videos for ${title}`}
                >
                  &lt;
                </button>
              )}
              <div className="video-section__grid">
                <VideoGrid videos={visibleVideos} onVideoClick={handleVideoClick} />
              </div>
              {hasMore && (
                <button
                  type="button"
                  className="more-button"
                  onClick={() => handleLoadMore(category)}
                  aria-label={`Load more videos for ${title}`}
                >
                  &gt;
                </button>
              )}
            </div>
          </section>
        );
      });
    }
    return sections;
  }, [loading, categorizedVideos, visibleOffsets, handleVideoClick, handleLoadMore, handleLoadPrevious]);

  return (
    <div className="home-layout">
      <Sidebar
        filters={hashtagFilters}
        onFilterChange={handleFilterChange}
        onReset={handleResetFilters}
      />
      <div className="home-content">
        {error && <div className="alert alert-danger">Error: {error}</div>}
        {loading && (
          <div className="text-center mt-5">
            <div className="spinner-border" role="status"></div>
            <div>Loading videos...</div>
          </div>
        )}
        {!loading && currentVideo && <VideoPlayer video={currentVideo} />}
        {!loading && hasActiveFilters && (
          <section className="video-section mb-5">
            <div className="video-section__header">
              <h3>Filtered Results</h3>
              <p className="filter-summary">
                {includeTags.length > 0 && (
                  <span>Yes: {includeTags.join(', ')} </span>
                )}
                {excludeTags.length > 0 && (
                  <span>No: {excludeTags.join(', ')} </span>
                )}
              </p>
            </div>
            <div className="video-section__content">
              <div className="video-section__grid">
                {filteredVideos.length > 0 ? (
                  <VideoGrid videos={filteredVideos} onVideoClick={handleVideoClick} />
                ) : (
                  <div className="alert alert-warning">No videos match the current filters.</div>
                )}
              </div>
            </div>
          </section>
        )}
        {!loading && !hasActiveFilters && videoSections}
      </div>
    </div>
  );
};

export default Home;
